import { RefObject, useLayoutEffect, useState } from "react"

const DEFAULT_GAP = 8
const DEFAULT_VIEWPORT_PADDING = 10

type PanelSize = {
  height: number
  width: number
}

interface UseAnchoredFloatingPanelPositionParams {
  anchorRef: RefObject<HTMLElement | null>
  panelSize: PanelSize
  gap?: number
  viewportPadding?: number
}

export const useAnchoredFloatingPanelPosition = ({
  anchorRef,
  panelSize,
  gap = DEFAULT_GAP,
  viewportPadding = DEFAULT_VIEWPORT_PADDING,
}: UseAnchoredFloatingPanelPositionParams) => {
  const [position, setPosition] = useState({ left: 0, top: 0 })

  useLayoutEffect(() => {
    const anchor = anchorRef.current
    if (!anchor || typeof window === "undefined") return

    const updatePosition = () => {
      if (!anchor.isConnected) return

      const rect = anchor.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const maxLeft = Math.max(viewportPadding, viewportWidth - panelSize.width - viewportPadding)

      const preferredLeft = rect.right - panelSize.width
      const fallbackLeft = rect.left
      const preferredTop = rect.bottom + gap
      const fallbackTop = rect.top - panelSize.height - gap

      let left = preferredLeft
      if (left < viewportPadding) {
        left = fallbackLeft
      }

      left = Math.min(Math.max(left, viewportPadding), maxLeft)
      let top = preferredTop

      if (top + panelSize.height > viewportHeight - viewportPadding && fallbackTop >= viewportPadding) {
        top = fallbackTop
      }

      if (top < viewportPadding) {
        top = viewportPadding
      }

      setPosition({ left, top })
    }

    updatePosition()

    const handleResize = () => updatePosition()
    const handleScroll = () => updatePosition()

    window.addEventListener("resize", handleResize)
    window.addEventListener("scroll", handleScroll, true)
    document.addEventListener("scroll", handleScroll, true)

    return () => {
      window.removeEventListener("resize", handleResize)
      window.removeEventListener("scroll", handleScroll, true)
      document.removeEventListener("scroll", handleScroll, true)
    }
  }, [anchorRef, gap, panelSize.height, panelSize.width, viewportPadding])

  return position
}
